import { useMemo } from 'react';
import { useSimpleStore } from '../stores/simpleStore';

export interface Permission {
  read: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
}

export interface MenuPermission {
  visible: boolean;
  accessible: boolean;
}

/**
 * 사용자 권한 관리 훅
 */
export const useAuth = () => {
  const { user, isAuthenticated } = useSimpleStore();

  const userPermissions = useMemo(() => {
    if (!user) return null;

    return {
      isAdmin: user.role === 'admin',
      isOperator: user.role === 'operator',
      companyId: user.company_id || null,
      companyName: user.company_name || null,
    };
  }, [user]);

  return {
    user,
    isAuthenticated,
    permissions: userPermissions,
    isAdmin: userPermissions?.isAdmin || false,
    isOperator: userPermissions?.isOperator || false,
    hasCompanyAccess: (companyId?: number | null) => {
      if (!userPermissions) return false;
      if (userPermissions.isAdmin) return true; // 관리자는 모든 운수사 접근 가능
      if (userPermissions.isOperator && userPermissions.companyId) {
        return companyId === userPermissions.companyId;
      }
      return false;
    },
  };
};

/**
 * 메뉴 권한 관리 훅
 */
export const useMenuPermissions = () => {
  const { isAdmin, isOperator } = useAuth();

  const menuPermissions = useMemo((): Record<string, MenuPermission> => {
    const basePermissions = {
      dashboard: { visible: true, accessible: true },
      routes: { visible: true, accessible: true },
      devices: { visible: true, accessible: true },
      media: { visible: true, accessible: true },
      playlists: { visible: true, accessible: true },
      'targeted-ads': { visible: true, accessible: true },
      'detour-notices': { visible: true, accessible: true },
      'hdmi-playlists': { visible: true, accessible: true },
      news: { visible: true, accessible: true },
      'app-config': { visible: true, accessible: true },
      profile: { visible: true, accessible: true },
    };

    // Admin 전용 메뉴
    const adminOnlyMenus = {
      companies: { visible: isAdmin, accessible: isAdmin },
      'system-settings': { visible: isAdmin, accessible: isAdmin },
      weather: { visible: isAdmin, accessible: isAdmin }, // 날씨 + 뉴스 관리는 admin 전용
      'apk-management': { visible: isAdmin, accessible: isAdmin }, // APK 버전 관리는 admin 전용
      disaster: { visible: isAdmin, accessible: isAdmin }, // 재난정보 관리는 admin 전용
    };

    // Operator 권한에 따른 메뉴 제한
    if (isOperator) {
      return {
        ...basePermissions,
        ...adminOnlyMenus,
      };
    }

    // Admin은 모든 메뉴 접근 가능
    if (isAdmin) {
      return {
        ...basePermissions,
        ...adminOnlyMenus,
      };
    }

    // 인증되지 않은 사용자는 아무것도 접근 불가
    return Object.keys({ ...basePermissions, ...adminOnlyMenus }).reduce(
      (acc, key) => {
        acc[key] = { visible: false, accessible: false };
        return acc;
      },
      {} as Record<string, MenuPermission>
    );
  }, [isAdmin, isOperator]);

  return {
    menuPermissions,
    canAccessMenu: (menuKey: string) => menuPermissions[menuKey]?.accessible || false,
    isMenuVisible: (menuKey: string) => menuPermissions[menuKey]?.visible || false,
  };
};

/**
 * 데이터 스코프 관리 훅
 */
export const useCompanyScope = () => {
  const { user } = useAuth();
  const { selectedCompanyId } = useSimpleStore();

  const scope = useMemo(() => {
    if (!user) return null;

    // Admin은 선택된 운수사 또는 전체
    if (user.role === 'admin') {
      return {
        companyId: selectedCompanyId,
        isGlobalScope: selectedCompanyId === null,
        isSingleCompany: selectedCompanyId !== null,
      };
    }

    // Operator는 자신의 운수사만
    if (user.role === 'operator' && user.company_id) {
      return {
        companyId: user.company_id,
        isGlobalScope: false,
        isSingleCompany: true,
      };
    }

    return null;
  }, [user, selectedCompanyId]);

  return {
    scope,
    getCompanyFilter: () => {
      if (!scope) return {};
      if (scope.isGlobalScope) return {};
      return { company_id: scope.companyId };
    },
    hasGlobalAccess: scope?.isGlobalScope || false,
    currentCompanyId: scope?.companyId || null,
  };
};

/**
 * 리소스별 권한 관리 훅
 */
export const useResourcePermissions = () => {
  const { isAdmin, isOperator, hasCompanyAccess } = useAuth();

  const getResourcePermissions = useMemo(() => {
    return (resourceType: string, companyId?: number | null): Permission => {
      // 인증되지 않은 사용자
      if (!isAdmin && !isOperator) {
        return { read: false, create: false, update: false, delete: false };
      }

      // Admin은 모든 권한
      if (isAdmin) {
        return { read: true, create: true, update: true, delete: true };
      }

      // Operator는 자신의 운수사 데이터만
      if (isOperator) {
        const hasAccess = hasCompanyAccess(companyId);
        
        // 운수사 관리는 읽기 전용
        if (resourceType === 'companies') {
          return { read: hasAccess, create: false, update: false, delete: false };
        }

        // 시스템 설정은 접근 불가
        if (resourceType === 'system-settings') {
          return { read: false, create: false, update: false, delete: false };
        }

        // 기타 리소스는 자신 운수사에 대해서만 전체 권한
        return {
          read: hasAccess,
          create: hasAccess,
          update: hasAccess,
          delete: hasAccess,
        };
      }

      return { read: false, create: false, update: false, delete: false };
    };
  }, [isAdmin, isOperator, hasCompanyAccess]);

  return {
    getResourcePermissions,
    canRead: (resourceType: string, companyId?: number | null) =>
      getResourcePermissions(resourceType, companyId).read,
    canCreate: (resourceType: string, companyId?: number | null) =>
      getResourcePermissions(resourceType, companyId).create,
    canUpdate: (resourceType: string, companyId?: number | null) =>
      getResourcePermissions(resourceType, companyId).update,
    canDelete: (resourceType: string, companyId?: number | null) =>
      getResourcePermissions(resourceType, companyId).delete,
  };
};